import React, { useState, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import QrCodeApi from '../services/qrCodeApi';
import SocketManager from '../utils/socketManager';
import { useSwiper } from 'swiper/react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import {
  getPromptPayNumber,
  getPromptPayName,
  savePromptPay,
  getQrCodeLogId,
  saveQrCodeLogId,
  saveStatusScan,
  getSubUserId,
  getSubUser
} from '../utils/auth';

// --- Improved Payload Generation Logic (to match Android) ---

const determinePromptPayIdType = (payeeId) => {
  const trimmedId = payeeId.trim();
  if (trimmedId.match(/^0[0-9]{9}$/)) {
    return 'PHONE_NUMBER';
  }
  if (trimmedId.match(/^[0-9]{13}$/)) {
    return 'NATIONAL_ID';
  }
  return 'E_WALLET_ID'; // Default
};

const appendTlv = (tag, value) => {
  const length = value.length.toString().padStart(2, '0');
  return `${tag}${length}${value}`;
};

const calculateCRC16 = (data) => {
  let crc = 0xFFFF;
  for (let i = 0; i < data.length; i++) {
    crc ^= data.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) {
      crc = (crc & 0x8000) ? ((crc << 1) ^ 0x1021) : (crc << 1);
    }
  }
  return (crc & 0xFFFF).toString(16).padStart(4, '0').toUpperCase();
};

const buildPromptPayPayload = (payeeId, amount) => {
  const idType = determinePromptPayIdType(payeeId);
  let merchantInfo = appendTlv("00", "A000000677010111"); // PromptPay GUID

  switch (idType) {
    case 'PHONE_NUMBER':
      const formattedPhone = "0066" + payeeId.trim().substring(1);
      merchantInfo += appendTlv("01", formattedPhone);
      break;
    case 'NATIONAL_ID':
      merchantInfo += appendTlv("02", payeeId.trim());
      break;
    case 'E_WALLET_ID':
      merchantInfo += appendTlv("03", payeeId.trim());
      break;
    default:
        return ""; // Should not happen
  }

  let payload = "";
  payload += appendTlv("00", "01"); // Payload Format Indicator
  payload += appendTlv("01", "11"); // Point of Initiation Method (11 for dynamic)
  payload += appendTlv("29", merchantInfo); // Merchant Information
  payload += appendTlv("53", "764"); // Currency Code THB
  
  if (amount && parseFloat(amount) > 0) {
    const amountStr = parseFloat(amount).toFixed(2);
    payload += appendTlv("54", amountStr);
  }

  payload += appendTlv("58", "TH"); // Country Code
  
  const payloadWithChecksumPlaceholder = payload + "6304";
  const checksum = calculateCRC16(payloadWithChecksumPlaceholder);
  
  return payloadWithChecksumPlaceholder + checksum;
};


const QrCodeComponent = () => {
  const swiper = useSwiper();
  // PROMPTPAY SETUP STATE
  const [promptPayConfigured, setPromptPayConfigured] = useState(getPromptPayNumber() != null && getPromptPayNumber() != 'null' && getPromptPayNumber() != '');
  const [promptPayName, setPromptPayName] = useState(getPromptPayName() || '');
  const [promptPayNumber, setPromptPayNumber] = useState(getPromptPayNumber() || '');

  // NUMBER PAD & QR LOGIC STATE
  const [amount, setAmount] = useState("");
  const [showQR, setShowQR] = useState(false);
  const [qrPayload, setQrPayload] = useState("");
  const [timer, setTimer] = useState(120); // seconds
  const timerRef = useRef(null);
  const [qrLogId, setQrLogId] = useState(getQrCodeLogId() || -1);
  const [showCloseButton, setShowCloseButton] = useState(false);

  // --------- Number Pad Handlers ---------
  const appendToAmount = (value) => {
    // Prevent multiple dots
    if (value === '.' && amount.includes('.')) return;
    setAmount(prev => prev + value);
  };

  const clearAmount = () => setAmount("");
  
  const formatAmount = (amt) => {
    const num = parseFloat(amt);
    if (isNaN(num)) return "0.00";
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // --------- Timer Functions ---------
  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setTimer(120); // Reset timer to 120s
    timerRef.current = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          updateQrCodeLog(); // This already resets the QR
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    setShowCloseButton(false);
    setTimeout(() => setShowCloseButton(true), 30000); // Show close button after 30s
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // --------- API Functions ---------
  const createQrCodeLog = async () => {
    const request = {
      userId: getSubUser()?.userId,
      subUserId: getSubUserId(),
      amount: parseFloat(amount) || 0
    };
    try {
      const response = await QrCodeApi.create(request);
      if (response.data && response.data !== 0) {
        saveQrCodeLogId(response.data);
        setQrLogId(response.data);
      }
    } catch (error) {
      alert("Failed to create QR Code Log");
      console.error(error);
    }
  };

  const updateQrCodeLog = async () => {
    const currentLogId = getQrCodeLogId();
    if (!currentLogId || currentLogId === -1) {
        resetQR(false);
        return;
    }
    const request = { id: currentLogId, status: "CANCEL" };
    try {
      await QrCodeApi.update(request);
    } catch (error) {
      console.error(error);
    } finally {
      resetQR(false);
    }
  };

  // --------- Socket.IO Setup ---------
  const setupSocketIO = () => {
    SocketManager.initializeSocket();
    SocketManager.addEventListener("scanQRCode", (data) => {
      console.log("QrCode Socket event:", data);
      data.forEach(item => {
        const notificationId = item.notificationId || -1;
        const qrCodeLogIdUpdate = item.id;
        const storedLogId = parseInt(getQrCodeLogId(), 10);
        if (notificationId > 0 && storedLogId === qrCodeLogIdUpdate) {
          toast.success("ชำระเงินสำเร็จ");
          resetQR(true);
          setTimeout(swiper.slideTo(0), 1000);
        }
      });
    });
    if (!SocketManager.isSocketConnected()) {
      SocketManager.reconnect();
    }
  };

  // --------- UI Transition Functions ---------
  const handleGenerateQR = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      alert("กรุณากรอกจำนวนเงินให้ถูกต้อง");
      return;
    }
    console.log("handleGenerateQR promptPayNumber:",promptPayNumber)
    const payload = buildPromptPayPayload(promptPayNumber, amount);
    setQrPayload(payload);
    setShowQR(true);
    startTimer();
    await createQrCodeLog();
    setupSocketIO();
  };

  const resetQR = (statusScan) => {
    saveStatusScan(statusScan);
    stopTimer();
    setShowQR(false);
    setAmount("");
    saveQrCodeLogId(-1);
    setQrLogId(-1);
    setQrPayload("");
  };
  
  const handleCancelQR = () => {
    // Optional: Add a confirmation dialog like in the Android app
    updateQrCodeLog();
  };

  // --------- RENDERING -----------
  if (!promptPayConfigured) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white p-5 text-center">
        <img src="/images/promptpay_logo.png" alt="PromptPay Logo" className="w-3/5 max-w-xs mb-5" />
        <div className="w-full max-w-sm p-5 mb-3 rounded-lg shadow">
          <img src="/images/alert.png" alt="Alert Logo" className="w-1/4 max-w-xs mb-5 mx-auto" />
          <label className="block text-gray-700" htmlFor="promptpay-name">บัญชีของคุณยังไม่ได้ตั้งค่าหมายเลขพร้อมเพย์</label>
          <label className="block text-gray-700" htmlFor="promptpay-name">กรุณาติดต่อผู้ดูแลระบบ</label>
          <label className="block text-gray-700" htmlFor="promptpay-name">เพื่อตั้งค่าหมายเลขพร้อมเพย์</label>
        </div>
      </div>
    );
  }

  if (!showQR) {
    return (
      <div className="flex flex-col items-center  min-h-screen bg-white p-4 w-full max-w-md mx-auto">
        <div className="w-full flex items-center justify-between p-4 ">
          <img src="/images/coin.png" alt="Coin" className="w-12 h-12" />
          <div className="text-4xl font-bold text-gray-800 mx-4">{formatAmount(amount) || '0.00'}</div>
          <img src="/images/thai_qr.png" alt="Thai QR Payment" className="w-12 h-12 cursor-pointer" onClick={handleGenerateQR} />
        </div>
        <div className="grid grid-cols-3 gap-4 w-full p-4">
          {['7', '8', '9', '4', '5', '6', '1', '2', '3', '0', '.', 'C'].map((val) => (
            <button
              key={val}
              onClick={() => (val === 'C' ? clearAmount() : appendToAmount(val))}
              className="py-6 text-2xl  bg-gray-200 rounded-lg shadow-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
            >
              {val}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
    <ToastContainer
        position="top-center"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    <div className="flex flex-col items-center justify-start min-h-screen bg-white p-4 w-full max-w-md mx-auto">
       <div className="w-full flex items-center justify-between p-2">
          <img src="/images/coin.png" alt="Coin" className="w-14 h-14" onClick={handleCancelQR} />
          <div className="text-4xl font-bold text-gray-800 mx-4">{formatAmount(amount)}</div>
          <div className="w-12 h-12 flex items-center justify-center">
            {showCloseButton && (
                <img src="/images/close.png" alt="Cancel" className="w-12 h-12 cursor-pointer" onClick={handleCancelQR}/>
            )}
          </div>
      </div>
      <div className="">
        <span className="text-gray-700">
            {String(Math.floor(timer / 60)).padStart(2, '0')}:{String(timer % 60).padStart(2, '0')}
        </span>
      </div>
      <div className="p-4 bg-white border-4 border-gray-200 rounded-lg shadow-lg">
        {qrPayload && <QRCodeCanvas value={qrPayload} size={256} />}
      </div>
       <div className="flex items-center justify-center mt-6 p-2 bg-blue-300 rounded-md">
          <img src="/images/thai_qr.png" alt="Thai QR" className="w-8 h-8" />
          <span className="ml-2 mr-2">{promptPayNumber}</span>
          <span className="">{promptPayName}</span>
       </div>
    </div>
    </>
  );
};

export default QrCodeComponent;